import React, { Suspense, useState } from 'react';
import type { Dayjs } from 'dayjs';
import { EyeIcon } from 'lucide-react';
import { ReactComponent as PdfIcon } from '@rocco/icons/svg/pdf.svg';
import { cn } from '@rocco/utils/cn';
import { Spinner } from '@rocco/components/spinner';
import type { MessageAttachment } from '../../types';
import { ChatLightbox } from '../lightbox/ChatLightbox';
import { ChatMessageAttachmentActions } from './ChatMessageAttachmentActions';

/**
 * Lazy-loaded PDF first-page renderer. Keeps `react-pdf` out of the SSR
 * module graph (pdf.js requires browser APIs like `DOMMatrix`).
 */
const LazyChatPDFPreviewPage = React.lazy(() => import('./ChatPDFPreviewPage'));

/**
 * Inline PDF preview card shown in the chat bubble when a message has exactly
 * one PDF attachment.
 * {@link ChatMessageBubble}
 */
export function ChatPDFPreview({
    className,
    attachment,
    date,
    isOptimistic,
}: {
    className?: string;
    attachment: MessageAttachment;
    date: Dayjs;
    isOptimistic: boolean;
}) {
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);

    return (
        <>
            <button
                type="button"
                className={cn(
                    'group relative w-full cursor-zoom-in overflow-hidden rounded-[8px] bg-white text-left text-black',
                    isOptimistic && 'pointer-events-none',
                    className,
                )}
                disabled={isOptimistic}
                onClick={() => {
                    setLightboxOpen(true);
                    setLightboxIndex(0);
                }}
            >
                <div
                    className={cn(
                        'flex items-center gap-[8px] p-[8px]',
                        !isOptimistic &&
                            'group-hover:bg-black/10 group-focus-visible:bg-black/10',
                    )}
                >
                    <PdfIcon className="size-[36px] shrink-0 p-[4px]" />
                    <div className="min-w-0 flex-1 text-[12px] leading-tight">
                        <div className="truncate font-medium">
                            {attachment.FileName ?? ''}
                        </div>
                        <div className="flex items-center gap-[4px] opacity-70">
                            <EyeIcon className="size-[12px]" />
                            <span>View</span>
                        </div>
                    </div>
                </div>
                {/* First page preview (lazy-loaded to avoid SSR issues). */}
                <div className="pointer-events-none relative h-[160px] overflow-hidden border-t border-black/10">
                    <Suspense>
                        {!isOptimistic && (
                            <LazyChatPDFPreviewPage url={attachment.Link} />
                        )}
                    </Suspense>
                    {isOptimistic && (
                        <div className="absolute inset-0 grid place-items-center bg-black/40">
                            <Spinner className="size-[20px] text-white" />
                        </div>
                    )}
                </div>
                {!isOptimistic && (
                    <ChatMessageAttachmentActions
                        className="absolute right-[6px] top-[6px] opacity-0 transition-opacity duration-100 group-hover:opacity-100"
                        url={attachment.Link}
                        filename={attachment.FileName ?? ''}
                    />
                )}
            </button>
            <ChatLightbox
                open={lightboxOpen}
                index={lightboxIndex}
                items={[
                    {
                        id: attachment.ID,
                        url: attachment.Link,
                        filename: attachment.FileName ?? '',
                        date,
                    },
                ]}
                onOpenChange={setLightboxOpen}
                onIndexChange={setLightboxIndex}
            />
        </>
    );
}
